/**
 * Reading and checking an answer document against a compiled product.
 *
 * Two jobs live here. `coerceAnswers` turns whatever the browser posted into
 * the shape the product describes - numbers as numbers, blanks as null,
 * anything the product does not ask for dropped. `checkRequired` answers the
 * question JSON Schema cannot: is a field that is *visible right now* filled
 * in. Both run on the server; the renderer calls `checkRequired` too, so the
 * user sees the same complaints before they submit as after.
 */
import { evaluate, isBlank } from "./conditions.js";
import { leafOf } from "./compile.js";
/** Every field the product asks for, by key. */
export function fieldIndex(product) {
    const index = new Map();
    for (const section of product.sections) {
        for (const field of section.fields)
            index.set(field.key, field);
    }
    return index;
}
/** The entities the product collects, and whether each repeats. */
export function entityPaths(product) {
    const paths = new Map();
    for (const section of product.sections)
        paths.set(section.path, section.repeating);
    return paths;
}
function fieldsByEntity(product) {
    const byEntity = new Map();
    for (const section of product.sections) {
        const list = byEntity.get(section.path) ?? [];
        list.push(...section.fields);
        byEntity.set(section.path, list);
    }
    return byEntity;
}
/** Turn one posted value into what its type says it is. An unparseable value
 *  is returned untouched so the validator can complain about it by name,
 *  rather than being silently nulled out. */
export function coerceValue(field, raw) {
    if (isBlank(raw))
        return null;
    switch (field.dataType) {
        case "boolean": {
            if (typeof raw === "boolean")
                return raw;
            const text = String(raw).trim().toLowerCase();
            if (["true", "yes", "y", "1"].includes(text))
                return true;
            if (["false", "no", "n", "0"].includes(text))
                return false;
            return raw;
        }
        case "integer":
        case "decimal":
        case "money":
        case "percent": {
            if (typeof raw === "number")
                return raw;
            // "1,250" and "$1,250.00" are both what someone types into a money box.
            const text = String(raw).replace(/[,$\s]/g, "");
            const parsed = Number(text);
            if (!Number.isFinite(parsed))
                return raw;
            if (field.dataType === "integer")
                return Math.trunc(parsed);
            const precision = field.validation?.precision;
            if (precision !== null && precision !== undefined) {
                return Number(parsed.toFixed(precision));
            }
            return parsed;
        }
        case "multi_choice": {
            const list = Array.isArray(raw) ? raw : [raw];
            return list.filter((item) => !isBlank(item)).map((item) => String(item));
        }
        case "date": {
            if (raw instanceof Date)
                return raw.toISOString().slice(0, 10);
            return String(raw).trim().slice(0, 10);
        }
        default:
            return typeof raw === "string" ? raw.trim() : raw;
    }
}
/**
 * Rebuild the answer document to the product's shape.
 *
 * Anything the product does not ask for is dropped rather than stored: the
 * compiled schema says `additionalProperties: false`, and keeping a stray key
 * around would mean the document could never validate again.
 */
export function coerceAnswers(product, answers) {
    const byEntity = fieldsByEntity(product);
    const repeating = entityPaths(product);
    const output = {};
    for (const [path, fields] of byEntity) {
        const incoming = answers?.[path];
        if (repeating.get(path)) {
            const rows = Array.isArray(incoming) ? incoming : [];
            output[path] = rows.map((row) => coerceRow(fields, row));
        }
        else {
            output[path] = coerceRow(fields, incoming);
        }
    }
    return output;
}
function coerceRow(fields, row) {
    const source = (row && typeof row === "object" && !Array.isArray(row) ? row : {});
    const result = {};
    for (const field of fields) {
        const value = coerceValue(field, source[field.name]);
        if (value !== null)
            result[field.name] = value;
    }
    return result;
}
/**
 * Which fields a user must fill in, given what they have filled in so far.
 *
 * Visibility is evaluated first and requiredness only applies to what is
 * visible, because demanding an answer to a question the form never showed is
 * how a dynamic form becomes impossible to submit. Inside a repeating section
 * each row is judged on its own answers.
 */
export function checkRequired(product, answers) {
    const issues = [];
    const root = answers ?? {};
    for (const section of product.sections) {
        if (!evaluate(section.visibleWhen, { root }))
            continue;
        if (section.repeating) {
            const rows = Array.isArray(root[section.path]) ? root[section.path] : [];
            if (rows.length === 0) {
                if (section.fields.some((field) => field.required)) {
                    issues.push({ path: section.path, message: `${section.label}: add at least one entry.` });
                }
                continue;
            }
            rows.forEach((row, index) => {
                for (const field of section.fields) {
                    const issue = requiredIssue(field, row, root, section, `${section.path}.${index}.${field.name}`);
                    if (issue)
                        issues.push(issue);
                }
            });
        }
        else {
            const row = (root[section.path] ?? {});
            for (const field of section.fields) {
                const issue = requiredIssue(field, row, root, section, `${section.path}.${field.name}`);
                if (issue)
                    issues.push(issue);
            }
        }
    }
    return issues;
}
function requiredIssue(field, row, root, section, path) {
    if (!field.required || field.readOnly)
        return null;
    const visible = evaluate(field.visibleWhen, {
        root,
        scopeEntityKey: section.path,
        scopeRow: row,
    });
    if (!visible)
        return null;
    if (!isBlank(row[leafOf(field.key)]))
        return null;
    return { path, fieldKey: field.key, message: `${field.label} is required.` };
}
/**
 * Which fields the renderer should show, for one row of answers. The browser
 * uses this to draw the form and the server uses the same call inside
 * `checkRequired`, so a field can never be hidden in one place and demanded in
 * the other.
 */
export function visibleFields(section, root, row) {
    return section.fields.filter((field) => evaluate(field.visibleWhen, { root, scopeEntityKey: section.path, scopeRow: row }));
}
/** A blank document with every entity present, so the renderer never has to
 *  guard against a missing branch. */
export function blankAnswers(product) {
    const answers = {};
    for (const section of product.sections) {
        if (section.repeating) {
            answers[section.path] = [];
        }
        else {
            const row = (answers[section.path] ?? {});
            for (const field of section.fields) {
                if (field.defaultValue !== null && field.defaultValue !== undefined) {
                    row[field.name] = field.defaultValue;
                }
            }
            answers[section.path] = row;
        }
    }
    return answers;
}
/** A blank row for a repeating section, with the section's defaults applied. */
export function blankRow(section) {
    const row = {};
    for (const field of section.fields) {
        if (field.defaultValue !== null && field.defaultValue !== undefined) {
            row[field.name] = field.defaultValue;
        }
    }
    return row;
}
/**
 * Something to call an application by, found rather than configured: the first
 * answered text field in the first non-repeating section.
 *
 * A carrier will eventually want to nominate that field explicitly. Until then
 * this beats a list of identical-looking references - and it lives here rather
 * than on either side alone, so the list screen and the header never disagree
 * about what an application is called.
 */
export function headline(product, answers) {
    if (!product)
        return null;
    for (const section of product.sections) {
        if (section.repeating)
            continue;
        const values = (answers?.[section.path] ?? {});
        for (const field of section.fields) {
            const value = values[field.name];
            if (field.dataType === "text" && typeof value === "string" && value.trim())
                return value.trim();
        }
    }
    return null;
}
//# sourceMappingURL=answers.js.map